// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';

import { teal } from 'api';
import { hasOrderStatusFlow } from 'utils/constants';
import {
    FOLLOW_UP_ORDERS_FILTER,
    countByDueState,
    filterFollowUpsForUser,
    getOpenFollowUps,
} from './followUps.helpers';

// High enough to get every open follow-up in one request.
const FOLLOW_UPS_PAGE_SIZE = 500;

const DUE_CHIPS = {
    overdue: { label: 'Overskredet', color: 'error' },
    today: { label: 'I dag', color: 'warning' },
};

const formatDate = date => (date ? new Date(date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ingen dato');

const FollowUps = () => {
    const { organisationId } = useParams();
    const navigate = useNavigate();
    const user = useSelector(state => state.auth.user);
    const organisation = user?.organisations?.find(org => org.organisationId === organisationId);
    const statusFlowEnabled = hasOrderStatusFlow(organisation);

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [scope, setScope] = useState('mine');

    useEffect(() => {
        if (!statusFlowEnabled) {
            setLoading(false);
            return;
        }

        const fetchFollowUps = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await teal.getAllOrders(organisationId, {
                    filter: JSON.stringify(FOLLOW_UP_ORDERS_FILTER),
                    page: 0,
                    pageSize: FOLLOW_UPS_PAGE_SIZE,
                });
                setOrders(result?.orders || []);
            } catch (fetchError) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchFollowUps();
    }, [organisationId, statusFlowEnabled]);

    const allFollowUps = useMemo(() => getOpenFollowUps(orders), [orders]);
    const followUps = scope === 'mine' ? filterFollowUpsForUser(allFollowUps, user?.userId) : allFollowUps;
    const counts = countByDueState(followUps);

    if (!statusFlowEnabled) {
        return <Alert severity="info" sx={{ mt: 2 }}>Opfølgninger er ikke slået til for denne organisation.</Alert>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Typography variant="h5" sx={{ mr: 'auto' }}>Opfølgninger</Typography>
                { !loading && (
                    <Typography variant="body2" color="text.secondary">
                        { `${followUps.length} åbne` }
                        { counts.overdue ? ` · ${counts.overdue} overskredet` : '' }
                        { counts.today ? ` · ${counts.today} i dag` : '' }
                    </Typography>
                ) }
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={ scope }
                    onChange={ (event, value) => value && setScope(value) }
                    aria-label="Vis opfølgninger"
                >
                    <ToggleButton value="mine">Mine</ToggleButton>
                    <ToggleButton value="all">Alle</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            { loading && (
                <div className="App-section-loading">
                    <CircularProgress color="primary" size={ 40 } />
                </div>
            ) }

            { !loading && error && <Alert severity="error">Kunne ikke hente opfølgninger.</Alert> }

            { !loading && !error && followUps.length === 0 && (
                <Alert severity="success">
                    { scope === 'mine' ? 'Du har ingen åbne opfølgninger.' : 'Der er ingen åbne opfølgninger.' }
                </Alert>
            ) }

            { !loading && !error && followUps.length > 0 && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Dato</TableCell>
                                <TableCell>Opgave</TableCell>
                                <TableCell>Kunde</TableCell>
                                <TableCell>Ordre</TableCell>
                                <TableCell>Ansvarlig</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { followUps.map(followUp => (
                                <TableRow
                                    key={ followUp.orderId }
                                    hover
                                    onClick={ () => navigate(`/${organisationId}/access/orders/${followUp.orderId}`) }
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Box>{ formatDate(followUp.date) }</Box>
                                        { DUE_CHIPS[followUp.dueState] && (
                                            <Chip size="small" sx={{ mt: 0.5 }} { ...DUE_CHIPS[followUp.dueState] } />
                                        ) }
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ fontWeight: 600 }}>{ followUp.title || 'Opfølgning' }</Box>
                                        { followUp.notes && (
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{ followUp.notes }</Typography>
                                        ) }
                                    </TableCell>
                                    <TableCell>
                                        <Box>{ followUp.customerName }</Box>
                                        { followUp.customerEmail && followUp.customerEmail !== followUp.customerName && (
                                            <Typography variant="caption" color="text.secondary">{ followUp.customerEmail }</Typography>
                                        ) }
                                    </TableCell>
                                    <TableCell>{ followUp.orderNumber ? `#${followUp.orderNumber}` : '-' }</TableCell>
                                    <TableCell>{ followUp.responsible?.name || '-' }</TableCell>
                                </TableRow>
                            )) }
                        </TableBody>
                    </Table>
                </TableContainer>
            ) }
        </Box>
    );
};

export default FollowUps;
